// assets/product-custom-property.js
import { Component } from '@theme/component';
// Imported for module-evaluation ordering only: guarantees product-form-component is
// defined before installSubmitGate() runs at the bottom of this file.
import '@theme/product-form';

/**
 * Fired by variant-picker.js when a shopper changes a variant option.
 * @see assets/standard-events.d.ts -> ProductSelectEvent.eventName
 */
const PRODUCT_SELECT_EVENT = 'shopify:product:select';

/** Emoji / pictographic detection. Avoids \p{Emoji}, which also matches ASCII digits, # and *. */
const EMOJI_PATTERN = /\p{Extended_Pictographic}|\p{Regional_Indicator}|[\uFE0F\u200D]/u;

/**
 * Curly punctuation -> ASCII, so what the shopper sees is what the Arial Black print
 * pipeline can actually render. iOS smart punctuation is the usual source.
 */
const NORMALIZE_MAP = [
  [/[\u2018\u2019\u201A\u201B\u2032]/g, "'"],
  [/[\u201C\u201D\u201E\u201F\u2033]/g, '"'],
  [/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-'],
  [/\u2026/g, '...'],
  [/[\u00A0\u2007\u2009\u202F]/g, ' '],
];

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} [textInput] - The text input.
 * @property {HTMLInputElement} [fileInput] - The file input.
 * @property {HTMLElement} [characterCount] - The character count element.
 * @property {HTMLElement} [errorMessage] - The inline error element.
 * @property {HTMLElement} [field] - The field wrapper.
 * @property {HTMLElement} [container] - The whole block wrapper (hidden when gated off).
 * @property {HTMLElement} [fileSelected] - Shows the chosen filename.
 */

/**
 * A custom element that manages product custom properties.
 *
 * Adds to the stock behaviour: file uploads, text normalization, emoji rejection,
 * and show/hide driven by a variant option (e.g. only ask for base text when
 * "Base Type" is "Custom Text Base").
 *
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#gateOption) {
      document.addEventListener(PRODUCT_SELECT_EVENT, this.#onProductSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(PRODUCT_SELECT_EVENT, this.#onProductSelect);
  }

  /**
   * The submittable control this block owns. The checkbox variant is rendered by the
   * shared 'checkbox' snippet and carries no ref, hence the querySelector fallback.
   * @returns {HTMLInputElement | HTMLTextAreaElement | null}
   */
  get field() {
    return (
      this.refs.textInput ?? this.refs.fileInput ?? /** @type {HTMLInputElement | null} */ (this.querySelector('input, textarea'))
    );
  }

  get #gateOption() {
    return this.field?.dataset.gateOption || '';
  }

  /**
   * True when this block's control is submitted by the given form.
   * Uses the live .form property so the form="" attribute association is respected.
   * @param {HTMLFormElement} form
   */
  ownsFormField(form) {
    const field = this.field;
    return Boolean(field && field.form === form);
  }

  handleInput() {
    this.#normalize();
    this.#updateCharacterCount();
    // Keep native validity accurate as they type, but stay quiet visually.
    this.#syncValidity();
    this.#hideError();
  }

  handleFileChange() {
    const input = this.refs.fileInput;
    if (!input) return;

    const { fileSelected } = this.refs;
    const file = input.files?.[0];

    if (!file) {
      if (fileSelected) {
        fileSelected.hidden = true;
        fileSelected.textContent = '';
      }
      this.#syncValidity();
      this.#hideError();
      return;
    }

    const problem = this.#fileProblem(file);

    if (problem) {
      // Reject outright so a bad file can never reach the cart.
      input.value = '';
      if (fileSelected) {
        fileSelected.hidden = true;
        fileSelected.textContent = '';
      }
      input.setCustomValidity(problem);
      this.#showError(problem);
      return;
    }

    input.setCustomValidity('');
    this.#hideError();

    if (fileSelected) {
      const kb = file.size / 1024;
      const size = kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`;
      fileSelected.textContent = `${file.name} (${size})`;
      fileSelected.hidden = false;
    }
  }

  /**
   * Validates and surfaces the result inline. Skipped entirely when the control is
   * disabled, which is how the variant gate switches this field off.
   * @returns {{ valid: boolean, message: string }}
   */
  validate() {
    const field = this.field;
    if (!field || field.disabled) return { valid: true, message: '' };

    const message = this.#problem(field);

    if (message) {
      field.setCustomValidity(message);
      this.#showError(message);
      return { valid: false, message };
    }

    field.setCustomValidity('');
    this.#hideError();
    return { valid: true, message: '' };
  }

  focusField() {
    const field = this.field;
    if (!field) return;
    this.refs.container?.removeAttribute('hidden');
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {string} An error message, or '' when valid.
   */
  #problem(field) {
    const data = field.dataset;

    if (field.type === 'file') {
      const file = /** @type {HTMLInputElement} */ (field).files?.[0];
      if (!file) return field.required ? data.errorRequired || '' : '';
      return this.#fileProblem(file);
    }

    if (field.type === 'checkbox') {
      return field.required && !(/** @type {HTMLInputElement} */ (field).checked) ? data.errorRequired || '' : '';
    }

    const value = field.value.trim();
    if (field.required && value === '') return data.errorRequired || '';
    if (data.rejectEmoji && EMOJI_PATTERN.test(value)) return data.errorEmoji || '';

    return '';
  }

  /**
   * @param {File} file
   * @returns {string} An error message, or '' when valid.
   */
  #fileProblem(file) {
    const input = this.refs.fileInput;
    if (!input) return '';

    const maxBytes = Number(input.dataset.maxBytes) || 0;
    if (maxBytes > 0 && file.size > maxBytes) {
      const mb = Math.round(maxBytes / 1048576);
      return (input.dataset.errorFileSize || '').replace('[size]', String(mb));
    }

    // Validate by extension, not file.type: .ai has no stable MIME type and reports
    // variously as application/postscript, application/pdf, or empty.
    const allowed = (input.dataset.allowedExt || '')
      .split(',')
      .map((ext) => ext.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);

    if (allowed.length) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!allowed.includes(ext)) {
        return (input.dataset.errorFileType || '').replace('[types]', allowed.join(', '));
      }
    }

    return '';
  }

  /** Sets native validity without displaying anything. */
  #syncValidity() {
    const field = this.field;
    if (!field || field.disabled) return;
    field.setCustomValidity(this.#problem(field));
  }

  #normalize() {
    const input = this.refs.textInput;
    if (!input || !input.dataset.normalize) return;

    const original = input.value;
    let next = original;
    for (const [pattern, replacement] of NORMALIZE_MAP) {
      next = next.replace(pattern, replacement);
    }
    if (next === original) return;

    // Rewriting .value collapses the selection to the end, so restore the caret.
    const caret = input.selectionStart;
    input.value = next;
    if (caret !== null) {
      const position = Math.min(caret, next.length);
      input.setSelectionRange(position, position);
    }
  }

  /** @param {string} message */
  #showError(message) {
    const { errorMessage } = this.refs;
    this.field?.setAttribute('aria-invalid', 'true');
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.hidden = false;
  }

  #hideError() {
    const { errorMessage } = this.refs;
    this.field?.removeAttribute('aria-invalid');
    if (!errorMessage) return;
    errorMessage.hidden = true;
    errorMessage.textContent = '';
  }

  /**
   * Shows/hides and enables/disables this field based on the selected variant option.
   *
   * `disabled` is doing real work here: it bars the control from constraint validation
   * (a hidden-but-required control is unfocusable, so the browser would block submit
   * while showing nothing) and drops it from FormData, so no empty property is sent.
   * @param {Event & {selectedOptions?: {name: string, value: string}[], promise?: Promise<unknown>}} event
   */
  #onProductSelect = (event) => {
    // Variant pickers inside product cards describe a different surface.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // The deferred promise rejects with AbortError when options are clicked rapidly.
    event.promise?.catch(() => {});

    const optionName = this.#gateOption;
    if (!optionName) return;

    const field = this.field;
    if (!field) return;

    const match = event.selectedOptions?.find((option) => option.name === optionName);
    if (!match) return;

    const active = match.value === (field.dataset.gateValue || '');

    field.disabled = !active;
    // Hide the whole block, not just the input, so the heading is not left orphaned.
    if (this.refs.container) this.refs.container.hidden = !active;

    if (!active) {
      field.setCustomValidity('');
      this.#hideError();
    } else {
      this.#syncValidity();
    }
  };

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    // The file and checkbox variants have neither ref.
    if (!characterCount || !textInput) return;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    // Count by code point so a stray surrogate pair does not read as 2.
    const currentLength = Array.from(textInput.value).length;
    const maxLength = textInput.maxLength;

    characterCount.textContent = template
      .replace('[current]', currentLength.toString())
      .replace('[max]', maxLength.toString());
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);

/**
 * Gates add-to-cart on our custom constraints.
 *
 * Why patch rather than listen: Component delegates events at document with
 * {capture: true} from the first connectedCallback, so no listener we register can
 * reliably run before handleSubmit, and handleSubmit preventDefault()s and builds
 * FormData immediately. Returning from the add-to-cart click handler would not cancel
 * a native submit either. Patching the prototype is the only ordering-safe hook.
 *
 * Also covers quick-add's form, which carries novalidate and would otherwise skip
 * native constraint validation entirely.
 */
const PATCH_FLAG = '__customPropertyGatePatched';

function installSubmitGate() {
  const ProductFormComponent = customElements.get('product-form-component');
  const prototype = ProductFormComponent?.prototype;
  if (!prototype || prototype[PATCH_FLAG]) return;

  const original = prototype.handleSubmit;
  if (typeof original !== 'function') return;

  prototype[PATCH_FLAG] = true;

  /** @param {Event} event */
  prototype.handleSubmit = function (event) {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));

    if (form) {
      const blocked = [];
      for (const element of document.querySelectorAll('product-custom-property-component')) {
        const block = /** @type {ProductCustomProperty} */ (element);
        if (typeof block.ownsFormField !== 'function' || !block.ownsFormField(form)) continue;
        if (!block.validate().valid) blocked.push(block);
      }

      if (blocked.length) {
        event.preventDefault();
        blocked[0]?.focusField();
        // Surfaces the same text as a native bubble (works even on novalidate forms).
        form.reportValidity();
        return;
      }
    }

    return original.call(this, event);
  };
}

installSubmitGate();
